import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { 
  Typography, Paper, TextField, Button, Box, FormControl, 
  InputLabel, Select, MenuItem, FormControlLabel, Checkbox,
  Grid, Alert, Divider, Dialog, DialogTitle, DialogContent,
  DialogActions, FormHelperText, styled, Chip, IconButton, List,
  ListItem, ListItemText, ListItemSecondaryAction
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import api, { useAuthenticatedApi } from '../utils/api';
import ReturnStages from './ReturnStages';

// Custom styled TextField with red asterisk for required fields
const StyledTextField = styled(TextField)(`
  & .MuiFormLabel-asterisk {
    color: #d32f2f;
  }
`);

// Custom styled InputLabel with red asterisk for required fields
const StyledInputLabel = styled(InputLabel)(`
  & .MuiFormLabel-asterisk {
    color: #d32f2f;
  }
`);

const ReturnForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  const { user } = useAuth0();
  const authenticatedApi = useAuthenticatedApi();
  
  const [formData, setFormData] = useState({
    customer_name: '',
    order_number: '',
    invoice_number: '',
    seller_name: '',
    notification_date: null,
    items_to_return: '',
    return_date: null,
    payment_refunded: false,
    requires_refund: false,
    is_exchange_unit: false,
    replacement_product_sent: false,
    information: '',
    account_number: '',
    status: 'pending',
    additional_info: '',
    initiator_email: ''
  });
  
  // State for tracking validation errors
  const [validationErrors, setValidationErrors] = useState({});
  
  // No longer need to fetch dropdown data since we're using text fields directly
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // State for managing return stages
  const [openStageDialog, setOpenStageDialog] = useState(false);
  const [stageFormData, setStageFormData] = useState({
    description: '',
    status: 'in_progress',
    handler: '',
    notes: '',
    notification_email: ''
  });
  const [stageError, setStageError] = useState(null);
  const [stageSuccess, setStageSuccess] = useState(null);
  
  // State for managing notification emails
  const [notificationEmails, setNotificationEmails] = useState([]);
  const [emailError, setEmailError] = useState(null);
  const [emailSuccess, setEmailSuccess] = useState(null);
  const [availableEmails, setAvailableEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState('');

  // Only fetch return data when in edit mode
  useEffect(() => {
    // Only fetch available emails when user is available
    if (user?.email) {
      fetchAvailableEmails();
    }
    
    // If in edit mode, fetch the return data
    if (isEditMode) {
      fetchReturnData();
      fetchNotificationEmails();
    } else if (user) {
      // Auto-populate initiator_email with the authenticated user's email
      setFormData(prev => ({
        ...prev,
        initiator_email: user.email
      }));
      // Set default notification emails for new returns, with initiator's email first
      setNotificationEmails([
        {
          id: 'temp-initiator',
          email: user.email
        }
      ]);
    }
  }, [isEditMode, id, user]);
  
  const fetchReturnData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/returns/${id}`);
      
      // Convert string dates to Date objects for the date picker
      const returnData = {
        ...response.data,
        notification_date: response.data.notification_date ? new Date(response.data.notification_date) : null,
        return_date: response.data.return_date ? new Date(response.data.return_date) : null
      };
      
      setFormData(returnData);
    } catch (err) {
      console.error('Error fetching return data:', err);
      setError('Virhe palautuksen tietojen haussa. Yritä myöhemmin uudelleen.');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchNotificationEmails = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/api/returns/${id}/notification-emails`);
      
      // Check if initiator's email is already in the list
      if (formData.initiator_email && response.data.findIndex(email => email.email === formData.initiator_email) === -1) {
        // Add initiator's email to the beginning of the list if it's not already there
        setNotificationEmails([
          {
            id: 'initiator-email', // This is just for UI display, will be replaced with real ID when saved
            email: formData.initiator_email
          },
          ...response.data
        ]);
      } else {
        setNotificationEmails(response.data);
      }
    } catch (err) {
      console.error('Error fetching notification emails:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchAvailableEmails = async () => {
    try {
      if (!user?.email) {
        console.error('User email not available');
        return;
      }
      
      // Use the authenticated API to get authorized users - using the new non-admin-restricted endpoint
      const response = await authenticatedApi.get(`/api/notification-available-emails?email=${encodeURIComponent(user.email)}`);
      
      // Format the data to match the expected structure
      const formattedEmails = response.data.map(user => ({
        id: user.id,
        email: user.email,
        description: user.is_admin ? 'Admin' : 'Käyttäjä'
      }));
      
      setAvailableEmails(formattedEmails);
      console.log('Fetched authorized users for email selection:', formattedEmails);
    } catch (err) {
      console.error('Error fetching authorized users:', err);
    }
  };
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const handleDateChange = (date, field) => {
    setFormData(prev => ({
      ...prev,
      [field]: date
    }));
  };
  
  // Validate form fields
  const validateForm = () => {
    const errors = {};
    
    // Required fields validation
    if (!formData.customer_name.trim()) errors.customer_name = 'Asiakkaan nimi on pakollinen';
    if (!formData.notification_date) errors.notification_date = 'Ilmoituspäivämäärä on pakollinen';
    if (!formData.items_to_return.trim()) errors.items_to_return = 'Palautettavat tuotteet on pakollinen';
    
    // Email validation if provided
    if (formData.initiator_email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.initiator_email)) {
      errors.initiator_email = 'Virheellinen sähköpostiosoite';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      setError('Täytä kaikki pakolliset kentät');
      // Scroll to the first error field
      const firstErrorField = Object.keys(validationErrors)[0];
      const errorElement = document.getElementsByName(firstErrorField)[0];
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        errorElement.focus();
      }
      return;
    }
    
    // Ensure initiator email is in the notification emails list
    if (formData.initiator_email && notificationEmails.findIndex(email => email.email === formData.initiator_email) === -1) {
      setNotificationEmails(prev => [
        {
          id: `temp-initiator-${Date.now()}`,
          email: formData.initiator_email
        },
        ...prev
      ]);
    }
    
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      // Format dates for API
      const formattedData = {
        ...formData,
        notification_date: formData.notification_date ? formData.notification_date.toISOString().split('T')[0] : null,
        return_date: formData.return_date ? formData.return_date.toISOString().split('T')[0] : null
      };
      
      let returnId;
      
      if (isEditMode) {
        await api.put(`/api/returns/${id}`, formattedData);
        returnId = id;
        setSuccess('Palautus päivitetty onnistuneesti!');
      } else {
        // Check if user email is available
        if (!user?.email) {
          setError('Käyttäjän sähköpostia ei löydy. Kirjaudu uudelleen sisään.');
          return;
        }
        
        // Include email as query parameter for handler assignment
        // Use authenticatedApi instead of api to include the JWT token
        const response = await authenticatedApi.post(`/api/returns?email=${encodeURIComponent(user.email)}`, formattedData);
        console.log('New return created with handler (email):', user.email);
        returnId = response.data.id;
        setSuccess('Uusi palautus luotu onnistuneesti!');
        
        // Add notification emails for new return
        if (notificationEmails.length > 0) {
          for (const emailObj of notificationEmails) {
            try {
              await api.post(`/api/returns/${returnId}/notification-emails`, { email: emailObj.email });
            } catch (emailErr) {
              console.error('Error adding notification email:', emailErr);
            }
          }
        }
        
        // Reset form after successful creation
        setFormData({
          customer_name: '',
          order_number: '',
          invoice_number: '',
          seller_name: '',
          notification_date: null,
          items_to_return: '',
          return_date: null,
          payment_refunded: false,
          requires_refund: false,
          information: '',
          account_number: '',
          status: 'pending',
          additional_info: '',
          initiator_email: ''
        });
        setValidationErrors({});
        setNotificationEmails([]);
      }
      
      // Navigate back to home page after a short delay
      setTimeout(() => {
        navigate('/');
      }, 2000);
      
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('Virhe lomakkeen lähetyksessä. Tarkista tiedot ja yritä uudelleen.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCancel = () => {
    navigate('/returns');
  };
  
  // Functions for managing return stages
  const handleOpenStageDialog = () => {
    setOpenStageDialog(true);
    setStageError(null);
    setStageSuccess(null);
    setStageFormData({
      description: '',
      status: 'in_progress',
      handler: '',
      notes: ''
    });
  };
  
  const handleCloseStageDialog = () => {
    setOpenStageDialog(false);
  };
  
  const handleStageFormChange = (e) => {
    const { name, value } = e.target;
    setStageFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleStageSubmit = async (e) => {
    e.preventDefault();
    
    // No need to validate description as it's no longer in the UI
    // We'll set a default description based on the status
    
    try {
      setLoading(true);
      setStageError(null);
      
      // Create a default description based on the status
      const getDefaultDescription = (status) => {
        switch(status) {
          case 'initial': return 'Palautus aloitettu';
          case 'in_progress': return 'Palautus käsittelyssä';
          case 'waiting': return 'Palautus odottaa';
          case 'returned': return 'Tuote palautunut';
          case 'completed': return 'Palautus valmis';
          case 'rejected': return 'Palautus hylätty';
          default: return 'Palautuksen tila päivitetty';
        }
      };
      
      // Add default description based on the status
      const dataToSend = {
        ...stageFormData,
        description: getDefaultDescription(stageFormData.status)
      };
      
      // Use authenticatedApi instead of api to include the JWT token and email parameter
      const response = await authenticatedApi.post(
        `/api/returns/${id}/stages?email=${encodeURIComponent(user.email)}`, 
        dataToSend
      );
      
      console.log('Stage added with default description:', dataToSend.description);
      console.log('Stage added with handler (email):', user.email);
      
      // Update the form data with the new return status
      if (response.data && response.data.return_status) {
        setFormData(prev => ({
          ...prev,
          status: response.data.return_status
        }));
      }
      
      // If notification email was provided, add it to the return
      if (stageFormData.notification_email && 
          stageFormData.notification_email.trim() && 
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(stageFormData.notification_email)) {
        try {
          await api.post(`/api/returns/${id}/notification-emails`, { 
            email: stageFormData.notification_email 
          });
          // Refresh notification emails
          fetchNotificationEmails();
        } catch (emailErr) {
          console.error('Error adding notification email:', emailErr);
        }
      }
      
      setStageSuccess('Uusi vaihe lisätty onnistuneesti!');
      setTimeout(() => {
        setOpenStageDialog(false);
        setStageSuccess(null);
        // Force refresh of the return stages component
        const event = new Event('stageAdded');
        window.dispatchEvent(event);
        // Refresh the return data to get the updated status
        fetchReturnData();
      }, 1500);
    } catch (err) {
      console.error('Error adding stage:', err);
      setStageError('Virhe vaiheen lisäämisessä. Yritä myöhemmin uudelleen.');
    } finally {
      setLoading(false);
    }
  };
  
  // Functions for managing notification emails
  const handleAddEmail = async () => {
    if (!selectedEmail) {
      setEmailError('Valitse sähköposti listasta');
      return;
    }
    
    setEmailError(null);
    
    // Check if email is already in the list
    if (notificationEmails.some(e => e.email === selectedEmail)) {
      setEmailError('Sähköposti on jo lisätty tälle palautukselle');
      return;
    }
    
    if (isEditMode) {
      try {
        setLoading(true);
        
        if (!user?.email) {
          setEmailError('Käyttäjän sähköpostia ei löydy. Kirjaudu uudelleen sisään.');
          return;
        }
        
        // Use authenticatedApi with user email as query parameter
        const response = await authenticatedApi.post(
          `/api/returns/${id}/notification-emails?email=${encodeURIComponent(user.email)}`, 
          { email: selectedEmail }
        );
        
        setNotificationEmails(prev => [...prev, response.data]);
        setSelectedEmail('');
        setEmailSuccess('Sähköposti lisätty onnistuneesti!');
        setTimeout(() => setEmailSuccess(null), 3000);
        
        console.log('Notification email added by user:', user.email);
      } catch (err) {
        console.error('Error adding email:', err);
        if (err.response && err.response.status === 409) {
          setEmailError('Sähköposti on jo lisätty tälle palautukselle');
        } else {
          setEmailError('Virhe sähköpostin lisäämisessä');
        }
      } finally {
        setLoading(false);
      }
    } else {
      // For new returns, just add to local state
      const newEmailObj = {
        id: `temp-${Date.now()}`,
        email: selectedEmail
      };
      setNotificationEmails(prev => [...prev, newEmailObj]);
      setSelectedEmail('');
    }
  };
  
  const handleRemoveEmail = async (emailId, email) => {
    if (isEditMode) {
      try {
        setLoading(true);
        await api.delete(`/api/returns/${id}/notification-emails/${emailId}`);
        setNotificationEmails(prev => prev.filter(e => e.id !== emailId));
      } catch (err) {
        console.error('Error removing email:', err);
        setEmailError('Virhe sähköpostin poistamisessa');
      } finally {
        setLoading(false);
      }
    } else {
      // For new returns, just remove from local state
      setNotificationEmails(prev => prev.filter(e => e.id !== emailId));
    }
  };

  return (
    <div>
      <Typography variant="h4" component="h1" gutterBottom>
        {isEditMode ? 'Muokkaa palautusta' : 'Uusi palautus'}
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>
      )}
      
      <Paper elevation={3} sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {/* Customer name field */}
            <Grid item xs={12} md={6}>
              <StyledTextField
                fullWidth
                label="Asiakas"
                name="customer_name"
                value={formData.customer_name}
                onChange={handleChange}
                disabled={loading}
                placeholder="Asiakkaan nimi"
                required
                error={!!validationErrors.customer_name}
                helperText={validationErrors.customer_name}
                FormHelperTextProps={{
                  style: { color: validationErrors.customer_name ? '#d32f2f' : 'inherit' }
                }}
              />
            </Grid>
            
            {/* Seller name field */}
            <Grid item xs={12} md={6}>
              <StyledTextField
                fullWidth
                label="Myyjä"
                name="seller_name"
                value={formData.seller_name}
                onChange={handleChange}
                disabled={loading}
                placeholder="Myyjän nimi"
              />
            </Grid>
            
            {/* Order number field */}
            <Grid item xs={12} md={6}>
              <StyledTextField
                fullWidth
                label="Tilausnumero"
                name="order_number"
                value={formData.order_number}
                onChange={handleChange}
                disabled={loading}
                placeholder="Tilausnumero"
                error={!!validationErrors.order_number}
                helperText={validationErrors.order_number}
                FormHelperTextProps={{
                  style: { color: validationErrors.order_number ? '#d32f2f' : 'inherit' }
                }}
              />
            </Grid>
            
            {/* Invoice number field */}
            <Grid item xs={12} md={6}>
              <StyledTextField
                fullWidth
                label="Laskunumero"
                name="invoice_number"
                value={formData.invoice_number}
                onChange={handleChange}
                disabled={loading}
                placeholder="Laskunumero"
                error={!!validationErrors.invoice_number}
                helperText={validationErrors.invoice_number}
                FormHelperTextProps={{
                  style: { color: validationErrors.invoice_number ? '#d32f2f' : 'inherit' }
                }}
              />
            </Grid>
            
            {/* Notification date */}
            <Grid item xs={12} md={6}>
              <Typography variant="body2" gutterBottom>
                Ilmoitus palautuksesta pvm <span style={{ color: '#d32f2f' }}>*</span>
              </Typography>
              <DatePicker
                selected={formData.notification_date}
                onChange={(date) => handleDateChange(date, 'notification_date')}
                dateFormat="dd.MM.yyyy"
                className="form-control"
                disabled={loading}
                customInput={
                  <StyledTextField 
                    fullWidth 
                    variant="outlined"
                    placeholder="Valitse päivämäärä"
                    error={!!validationErrors.notification_date}
                    required
                  />
                }
              />
              {validationErrors.notification_date && (
                <FormHelperText error>{validationErrors.notification_date}</FormHelperText>
              )}
            </Grid>
            
            {/* Return date */}
            <Grid item xs={12} md={6}>
              <Typography variant="body2" gutterBottom>
                Tuote palautunut pvm
              </Typography>
              <DatePicker
                selected={formData.return_date}
                onChange={(date) => handleDateChange(date, 'return_date')}
                dateFormat="dd.MM.yyyy"
                className="form-control"
                disabled={loading}
                customInput={
                  <StyledTextField 
                    fullWidth 
                    variant="outlined"
                    placeholder="Valitse päivämäärä"
                  />
                }
              />
            </Grid>
            
            {/* Items to return */}
            <Grid item xs={12}>
              <StyledTextField
                fullWidth
                label="Mitä palautetaan"
                name="items_to_return"
                multiline
                rows={3}
                value={formData.items_to_return}
                onChange={handleChange}
                disabled={loading}
                required
                error={!!validationErrors.items_to_return}
                helperText={validationErrors.items_to_return}
                FormHelperTextProps={{
                  style: { color: validationErrors.items_to_return ? '#d32f2f' : 'inherit' }
                }}
              />
            </Grid>
            
            {/* Information */}
            <Grid item xs={12} md={6}>
              <StyledTextField
                fullWidth
                label="Tiedot"
                name="information"
                value={formData.information}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>
            
            {/* Account number */}
            <Grid item xs={12} md={6}>
              <StyledTextField
                fullWidth
                label="Tilinumero"
                name="account_number"
                value={formData.account_number}
                onChange={handleChange}
                disabled={loading}
                placeholder="FI00 0000 0000 0000 00"
              />
            </Grid>
            
            {/* Status */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <StyledInputLabel id="status-label">Tila</StyledInputLabel>
                <Select
                  labelId="status-label"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  label="Tila"
                  disabled={loading}
                >
                  <MenuItem value="pending">Käsittelyssä</MenuItem>
                  <MenuItem value="completed">Valmis</MenuItem>
                  <MenuItem value="rejected">Hylätty</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Payment refunded */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.payment_refunded}
                      onChange={handleChange}
                      name="payment_refunded"
                      disabled={loading}
                    />
                  }
                  label="Maksu palautettu"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.requires_refund}
                      onChange={handleChange}
                      name="requires_refund"
                      disabled={loading}
                      color="secondary"
                    />
                  }
                  label="Vaatii rahanpalautuksen"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.is_exchange_unit}
                      onChange={handleChange}
                      name="is_exchange_unit"
                      disabled={loading}
                      color="primary"
                    />
                  }
                  label="Vaihtoyksikkö"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.replacement_product_sent}
                      onChange={handleChange}
                      name="replacement_product_sent"
                      disabled={loading}
                      color="info"
                    />
                  }
                  label="Korvaava tuote lähetetty"
                />
              </Box>
            </Grid>
            
            {/* Initiator email - auto-populated from Auth0 */}
            <Grid item xs={12} md={6}>
              <StyledTextField
                fullWidth
                label="Palautuksen aloittaja (sähköposti)"
                name="initiator_email"
                value={formData.initiator_email}
                onChange={handleChange}
                disabled={true} // Disabled to prevent manual changes
                placeholder="esimerkki@email.com"
                type="email"
                required
                helperText="Täytetään automaattisesti kirjautuneen käyttäjän sähköpostilla"
              />
            </Grid>
            
            {/* Notification emails */}
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: '#f9f9f9' }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Palautuksen sähköpostit
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <FormControl fullWidth sx={{ mr: 1 }}>
                    <InputLabel id="available-emails-label">Valitse sähköposti listasta</InputLabel>
                    <Select
                      labelId="available-emails-label"
                      value={selectedEmail}
                      onChange={(e) => setSelectedEmail(e.target.value)}
                      label="Valitse sähköposti listasta"
                      size="small"
                      disabled={loading}
                      error={!!emailError}
                    >
                      <MenuItem value=""><em>Valitse sähköposti</em></MenuItem>
                      {availableEmails.map((emailObj) => (
                        <MenuItem key={emailObj.id} value={emailObj.email}>
                          {emailObj.description ? `${emailObj.email} (${emailObj.description})` : emailObj.email}
                        </MenuItem>
                      ))}
                    </Select>
                    {emailError && <FormHelperText error>{emailError}</FormHelperText>}
                  </FormControl>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleAddEmail}
                    disabled={loading || !selectedEmail}
                  >
                    Lisää
                  </Button>
                </Box>
              
              {emailSuccess && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {emailSuccess}
                </Alert>
              )}
              
              {emailSuccess && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {emailSuccess}
                </Alert>
              )}
              
              <Typography variant="subtitle2" gutterBottom>
                Sähköpostit, joille lähetetään ilmoitukset:
              </Typography>
              
              {notificationEmails.length > 0 ? (
                <List dense sx={{ bgcolor: 'white', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  {notificationEmails.map((emailObj, index) => (
                    <ListItem 
                      key={emailObj.id} 
                      divider={index < notificationEmails.length - 1}
                      sx={{
                        bgcolor: index === 0 ? '#e3f2fd' : 'white', // Highlight the first email (initiator)
                      }}
                    >
                      <ListItemText 
                        primary={emailObj.email} 
                        secondary={index === 0 ? 'Palautuksen aloittaja' : null}
                      />
                      <ListItemSecondaryAction>
                        <IconButton 
                          edge="end" 
                          aria-label="delete"
                          onClick={() => handleRemoveEmail(emailObj.id, emailObj.email)}
                          disabled={loading || index === 0} // Prevent removing the initiator's email
                          sx={{ color: index === 0 ? 'rgba(0, 0, 0, 0.26)' : 'inherit' }} // Gray out for initiator
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  Ei sähköposteja lisätty
                </Typography>
              )}
              </Paper>
            </Grid>

            {/* Additional info */}
            <Grid item xs={12}>
              <StyledTextField
                fullWidth
                label="Lisätiedot"
                name="additional_info"
                multiline
                rows={3}
                value={formData.additional_info}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>
            
            {/* Required fields note */}
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ display: 'block', mt: 2, color: '#d32f2f' }}>
                * Pakollinen kenttä
              </Typography>
            </Grid>
            
            {/* Form buttons */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  Peruuta
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                >
                  {loading ? 'Tallennetaan...' : (isEditMode ? 'Päivitä' : 'Tallenna')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Return Stages Section - Only show in edit mode */}
      {isEditMode && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4, mb: 2 }}>
            <Typography variant="h5">Palautuksen vaiheet</Typography>
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleOpenStageDialog}
            >
              Lisää uusi vaihe
            </Button>
          </Box>
          
          <ReturnStages returnId={id} key={`stages-${id}`} />
        </>
      )}

      {/* Dialog for adding a new stage */}
      <Dialog open={openStageDialog} onClose={handleCloseStageDialog} maxWidth="md" fullWidth>
        <DialogTitle>Lisää uusi vaihe palautukselle</DialogTitle>
        <DialogContent>
          {stageError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {stageError}
            </Alert>
          )}
          {stageSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {stageSuccess}
            </Alert>
          )}
          <Box component="form" onSubmit={handleStageSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              {/* Description field removed as requested */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <StyledInputLabel id="stage-status-label">Tila</StyledInputLabel>
                  <Select
                    labelId="stage-status-label"
                    name="status"
                    value={stageFormData.status}
                    onChange={handleStageFormChange}
                    label="Tila"
                    disabled={loading}
                  >
                    <MenuItem value="initial">Aloitettu</MenuItem>
                    <MenuItem value="in_progress">Käsittelyssä</MenuItem>
                    <MenuItem value="waiting">Odottaa</MenuItem>
                    <MenuItem value="returned">Palautunut</MenuItem>
                    <MenuItem value="completed">Valmis</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {/* Handler field removed - will use Auth0 email automatically */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="stage-email-label">Lisää sähköposti ilmoituslistalle</InputLabel>
                  <Select
                    labelId="stage-email-label"
                    name="notification_email"
                    value={stageFormData.notification_email}
                    onChange={handleStageFormChange}
                    label="Lisää sähköposti ilmoituslistalle"
                    disabled={loading}
                  >
                    <MenuItem value=""><em>Ei sähköpostia</em></MenuItem>
                    {availableEmails.map((emailObj) => (
                      <MenuItem key={emailObj.id} value={emailObj.email}>
                        {emailObj.description ? `${emailObj.email} (${emailObj.description})` : emailObj.email}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Voit lisätä sähköpostin palautuksen ilmoituslistalle</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <StyledTextField
                  fullWidth
                  label="Lisätiedot"
                  name="notes"
                  multiline
                  rows={3}
                  value={stageFormData.notes}
                  onChange={handleStageFormChange}
                  disabled={loading}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStageDialog} disabled={loading}>
            Peruuta
          </Button>
          <Button 
            onClick={handleStageSubmit} 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? 'Tallennetaan...' : 'Tallenna'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ReturnForm;
